import axios from 'axios';
import {
  flow,
  getEnv,
  getRoot,
  getSnapshot,
  onSnapshot,
  types,
} from 'mobx-state-tree';

import CheckoutPageModule, {
  CheckoutPageModuleType,
} from '../../../types/checkout/CheckoutPageModule';
import FormFieldId from '../../../types/FormFieldId';
import FormFieldType from '../../../types/FormFieldType';
import OrderCheckoutType, {
  OrderCheckoutTypeType,
} from '../../../types/order/OrderCheckoutType';
import OrderPaymentProvider from '../../../types/order/OrderPaymentProvider';
import RequestState, { RequestStateType } from '../../../types/RequestState';
import { invalidFormFields } from '../../../util/constants';
import { stringify } from '../../../util/queryString';
import CartBasket from '../../cart/CartBasket';
import CustomFormField from '../../CustomFormField';
import Filter from '../../Filter';
import PickupPoint from '../../shipping/PickupPoint';
import Order from '../Order';
import OrderShippingOption from '../OrderShippingOption';
import OrderCheckoutProduct from './OrderCheckoutProduct';
import PaymentMethodGroup from './payment/PaymentMethodGroup';
import PaymentProvider from './payment/PaymentProvider';
import SelectedPayment from './payment/SelectedPayment';
import SelectedShipping from './shipping/SelectedShipping';

const isApplePayMethod = (option) => {
  return option?.id === 'applepay' && option?.parent_group_type === 'CUSTOM';
};

const OrderCheckoutModel = types
  .compose(
    Order,
    types.model('OrderCheckout', {
      after_confirm_redirect_url: types.maybeNull(types.string),
      available_filters: types.maybeNull(types.array(Filter)),
      baskets_and_templates: types.maybeNull(types.array(CartBasket)),
      checkout_type: OrderCheckoutTypeType,
      currency: types.string,
      customer_fields: types.maybeNull(types.map(CustomFormField)),
      extra_fields: types.maybeNull(types.map(CustomFormField)),
      free_of_charge_product_info: types.optional(
        types.model('FreeOfChargeProductInfo', {
          max_products: types.number,
          min_order: types.model({
            with_tax: types.number,
            without_tax: types.number,
          }),
        }),
        {
          max_products: 0,
          min_order: {
            with_tax: 0,
            without_tax: 0,
          },
        }
      ),
      gift_voucher_recipient_email: types.maybeNull(types.string),
      has_gift_vouchers: types.boolean,
      http_widget: types.maybeNull(types.string),
      id_for_analytics: types.string,
      info_pages: types.optional(
        types.model({
          DELIVERY_TERMS: types.maybeNull(types.integer),
          PRIVACY_TERMS: types.maybeNull(types.integer),
        }),
        {}
      ),
      payment_method_groups: types.maybeNull(types.array(PaymentMethodGroup)),
      payment_provider_module_id: types.integer,
      payment_providers: types.maybeNull(types.array(PaymentProvider)),
      /**
       * Pickup Point Storage:
       * - pickupPoints: Map of pickup point lists, keyed by pickupPointIdentifier
       *   - Not from backend, only set when fetching occurs
       *   - Clears when user navigates back/forth (which is ok)
       *   - Each identifier stores a list of pickup points for that specific combination
       * - pickupPointState: Map of request states (LOADING, LOADED, ERROR, NONE) per identifier
       *   - Tracks the fetch state for each identifier
       */
      pickupPoints: types.optional(types.map(types.array(PickupPoint)), {}),
      pickupPointState: types.optional(
        types.map(types.optional(RequestStateType, RequestState.NONE)),
        {}
      ),
      /**
       * Preserved Pickup Point ID:
       * - Stores the selected pickup point ID to preserve it across provider changes
       * - Used to maintain selection when switching between payment providers
       * - Cleared when shipping module changes (not just provider)
       * - If preserved ID exists in new list after fetch, it's automatically selected
       */
      preservedPickupPointId: types.maybeNull(types.string),
      /**
       * Pickup Point Needs Backend Sync:
       * - Volatile flag set when handlePickupPointsLoaded clears/changes pickup point
       * - Checked by CheckoutStore after fetch to determine if PUT is needed
       */
      _pickupPointNeedsSync: types.optional(types.boolean, false),
      products: types.maybeNull(types.array(OrderCheckoutProduct)),
      real_total: types.number,
      removed_products: types.maybeNull(types.array(OrderCheckoutProduct)),
      scroll_to_alert: types.maybeNull(CheckoutPageModuleType),
      scroll_to_method: types.maybeNull(types.string),
      selected_basket_id: types.string,
      selected_payment: types.optional(SelectedPayment, {
        group: null,
        method: null,
        provider: null,
      }),
      selected_shipping: types.maybeNull(SelectedShipping),
      shipping_customer_fields: types.maybeNull(types.map(CustomFormField)),
      shipping_options: types.maybeNull(types.array(OrderShippingOption)),
      show_free_of_charge_products: types.boolean,
      show_recommended_products: types.boolean,
      show_registration: types.boolean,
      submit_button_type: types.string,
      total_weight: types.maybeNull(types.string),
    })
  )
  .named('OrderCheckout')
  .volatile(() => ({
    applePaySupportCheckCompleted: false,
    applePaySupported: false,
  }))
  .extend((self) => {
    const nonTextFieldTypes = new Set([
      FormFieldType.BOOLEAN,
      FormFieldType.BUTTON,
    ]);
    const getSeparateShippingAddress = () => {
      const deliveryPostalCode = self.customer_fields.get(
        FormFieldId.DELIVERY_POSTAL_CODE
      )?.value;
      if (deliveryPostalCode) {
        return {
          postCode: self.customer_fields.get(FormFieldId.DELIVERY_POSTAL_CODE)
            ?.value,
          city: self.customer_fields.get(FormFieldId.DELIVERY_CITY)?.value,
          streetAddress: self.customer_fields.get(
            FormFieldId.DELIVERY_STREET_ADDRESS
          )?.value,
        };
      }

      return {};
    };

    const getPrimaryShippingAddress = () => {
      const primaryPostalCode = self.customer_fields.get(
        FormFieldId.BILLING_POSTAL_CODE
      )?.value;
      if (primaryPostalCode) {
        return {
          postCode: self.customer_fields.get(FormFieldId.BILLING_POSTAL_CODE)
            ?.value,
          city: self.customer_fields.get(FormFieldId.BILLING_CITY)?.value,
          streetAddress: self.customer_fields.get(
            FormFieldId.BILLING_STREET_ADDRESS
          )?.value,
        };
      }

      return {};
    };

    const getShippingCustomerAddress = () => {
      const primaryPostalCode = self.shipping_customer_fields.get(
        FormFieldId.DELIVERY_POSTAL_CODE
      )?.value;
      if (primaryPostalCode) {
        return {
          postCode: self.shipping_customer_fields.get(
            FormFieldId.DELIVERY_POSTAL_CODE
          )?.value,
          streetAddress: self.shipping_customer_fields.get(
            FormFieldId.DELIVERY_STREET_ADDRESS
          )?.value,
        };
      }

      return {};
    };

    const getShippingAddress = () => {
      const isSeparateCustomerAddress = self.customer_fields.get(
        FormFieldId.SEPARATE_DELIVERY_ADDRESS
      )?.value;
      let address = {};

      if (isSeparateCustomerAddress) {
        address = getSeparateShippingAddress();
      } else {
        address = getPrimaryShippingAddress();
      }

      return Object.keys(address).length > 0 ? address : null;
    };

    const getShippingCustomerFieldAddress = () => {
      const address = getShippingCustomerAddress();

      return Object.keys(address).length > 0 ? address : null;
    };
    const getSelectedShippingModule = () => {
      if (!self.selected_shipping) {
        return null;
      }
      try {
        // safeReference should return null if unresolved, but wrap in try-catch
        // to handle cases where the reference is invalid or can't be resolved
        // Access the module property directly - safeReference will throw if invalid
        const module = self.selected_shipping.module;
        // If module exists and is resolved, return it; otherwise return null
        return module || null;
      } catch (error) {
        // Reference is invalid/unresolved (e.g., shipping option no longer exists)
        // Return null to prevent errors from propagating
        return null;
      }
    };
    const getSelectedPaymentModule = () => self.payment_provider_module_id;

    let source;
    return {
      views: {
        get billingFields() {
          return self.customerFieldsList.filter(
            (field) =>
              field.id.startsWith('BILLING_') &&
              !invalidFormFields.checkout.has(field.type)
          );
        },
        get cartAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.CART_MODULE);
        },
        get cartIsEmpty() {
          return (
            self.products.length === 0 ||
            self.products.every((product) => product.quantity === 0)
          );
        },
        get isB2BCheckout() {
          return self.checkout_type === OrderCheckoutType.B2B;
        },
        get countryField() {
          return self.shippingCustomerFields.filter(
            (field) => field.id === 'DELIVERY_COUNTRY'
          );
        },
        get customerAddress() {
          let address = null;

          switch (this.paymentProvider) {
            case OrderPaymentProvider.KUSTOM:
              address = getShippingCustomerFieldAddress();
              break;
            default:
              address = getShippingAddress();
              break;
          }
          return address;
        },
        get customerFieldsList() {
          return [...self.customer_fields.values()];
        },
        get customerCountryAlerts() {
          return self.getModuleAlerts(
            CheckoutPageModule.CUSTOMER_COUNTRY_MODULE
          );
        },
        get customerInfoAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.CUSTOMER_INFO_MODULE);
        },
        /**
         * Current Pickup Point List:
         * Returns the pickup points for the current identifier.
         * Returns empty array if no data is cached for the current identifier.
         */
        get currentPickupPointList() {
          const identifier = self.pickupPointIdentifier;
          return self.pickupPoints.get(identifier) || [];
        },
        /**
         * Current Pickup Point State:
         * Returns the request state (LOADING, LOADED, ERROR, NONE) for the current identifier.
         */
        get currentPickupPointState() {
          return self.pickupPointState.get(self.pickupPointIdentifier);
        },
        get deliveryFields() {
          return self.customerFieldsList.filter(
            (field) =>
              field.id.startsWith('DELIVERY_') &&
              !nonTextFieldTypes.has(field.type)
          );
        },
        get externalPaymentMethodAlerts() {
          return self.getModuleAlerts(
            CheckoutPageModule.EXTERNAL_CHECKOUT_MODULE
          );
        },
        get extraFields() {
          return [...self.extra_fields.values()];
        },
        get hasCountryOptions() {
          return (
            self.getShippingCustomerField(FormFieldId.DELIVERY_COUNTRY).field
              ?.options.length > 0 || false
          );
        },
        get hasCustomerFields() {
          return self.customer_fields?.size > 0 || false;
        },
        get hasPaymentProviders() {
          return self.payment_providers && self.payment_providers.length > 0;
        },
        get hasShippingOptions() {
          return self.shipping_options && self.shipping_options.length > 0;
        },
        get paymentProvider() {
          return self.selected_payment?.provider || null;
        },
        get shouldUseProviderCustomerHandling() {
          return self.paymentProvider === OrderPaymentProvider.KUSTOM;
        },
        get hasTerms() {
          return (
            self.info_pages.DELIVERY_TERMS !== null ||
            self.info_pages.PRIVACY_TERMS !== null
          );
        },
        get paymentMethodGroups() {
          const originalGroups = self.payment_method_groups || [];
          const groups = originalGroups.filter(
            (group) => group.type !== 'CUSTOM'
          );
          const customGroups = originalGroups
            .filter((group) => group.type === 'CUSTOM')
            .reduce((acc, group) => acc.concat(group.methods), []);

          return {
            groups,
            customGroups: this.shouldHideApplePay
              ? customGroups.filter((method) => !isApplePayMethod(method))
              : customGroups,
          };
        },
        get selectedPaymentGroupMethods() {
          const selectedGroup = self.selected_payment?.group;

          if (!selectedGroup) {
            return [];
          }

          return this.shouldHideApplePay
            ? selectedGroup.methods.filter(
                (method) => !isApplePayMethod(method)
              )
            : selectedGroup.methods;
        },
        get shouldDelayVerifonePaymentMethods() {
          return (
            this.paymentProvider === OrderPaymentProvider.VERIFONE &&
            !self.applePaySupportCheckCompleted
          );
        },
        get shouldHideApplePay() {
          return (
            this.paymentProvider === OrderPaymentProvider.VERIFONE &&
            self.applePaySupportCheckCompleted &&
            !self.applePaySupported
          );
        },
        get paymentMethodAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.PAYMENT_METHOD_MODULE);
        },
        /**
         * Pickup Point Identifier:
         * Creates a unique key for caching pickup points based on:
         * - Shipping module ID (selected_shipping.module.id)
         * - Payment module ID (payment_provider_module_id)
         * - Country code (from countryStore)
         * - Customer address (postCode, city, streetAddress - varies by provider)
         *   - KUSTOM: uses shipping_customer_fields (DELIVERY_POSTAL_CODE, DELIVERY_STREET_ADDRESS)
         *   - PAYTRAIL: uses customer_fields (BILLING or DELIVERY based on SEPARATE_DELIVERY_ADDRESS)
         *
         * This identifier changes when any of these values change, triggering a new fetch
         * if the identifier doesn't exist in the cache.
         */
        get pickupPointIdentifier() {
          const selectedShippingModule = getSelectedShippingModule();
          const selectedPaymentModule = getSelectedPaymentModule();
          const countryCode =
            getRoot(self).countryStore?.activeCountry?.iso_code_2;
          return stringify({
            shippingModuleId: selectedShippingModule?.id,
            paymentModuleId: selectedPaymentModule,
            countryCode,
            ...self.customerAddress,
          });
        },
        get pickupPointIsLoading() {
          return (
            self.pickupPointState.get(self.pickupPointIdentifier) ===
            RequestState.LOADING
          );
        },
        get productList() {
          return self.products;
        },
        get recommendedProductsIds() {
          return self.products.map((product) => product.product_id);
        },
        get selectedPayment() {
          return {
            provider: self.selected_payment?.provider || null,
            group: self.selected_payment?.group?.type || null,
            method: self.selected_payment?.method?.id || null,
          };
        },
        /**
         * Selected Pickup Point:
         * Returns the full pickup point object that matches selected_shipping.pickup_point.
         * Used by PickupPointList component to determine which radio button should be selected.
         *
         * Search strategy:
         * 1. First searches in currentPickupPointList (for current identifier)
         * 2. If not found, searches all cached pickup point lists
         *    (This handles cases where provider changed but new list hasn't loaded yet,
         *     or when the ID exists in a different cached list)
         *
         * Returns null if no pickup point is selected or if the selected ID doesn't exist in any list.
         */
        get selectedPickupPoint() {
          if (!self.selected_shipping?.pickup_point) {
            return null;
          }

          const selectedId = self.selected_shipping.pickup_point;

          // First, try to find in current list (most common case)
          const currentPickupPoint = self.currentPickupPointList.find(
            (pp) => pp.unique_id === selectedId
          );
          if (currentPickupPoint) {
            return currentPickupPoint;
          }

          // If not found in current list, search all cached pickup point lists
          // This handles cases where:
          // - Provider changed but new list hasn't loaded yet
          // - The ID exists in a previously cached list
          for (const [
            identifier,
            pickupPointList,
          ] of self.pickupPoints.entries()) {
            const foundPickupPoint = pickupPointList.find(
              (pp) => pp.unique_id === selectedId
            );
            if (foundPickupPoint) {
              return foundPickupPoint;
            }
          }

          return null;
        },
        /**
         * Selected Shipping:
         * Formats selected shipping data for backend API.
         *
         * Backend expects pickup_point as an object with all pickup point fields:
         * - unique_id (required)
         * - name, address, opening_hours (nullable)
         * - external_info (nullable, array of {key, value} objects)
         * - location_info (nullable)
         *
         * This is used in the checkout PUT request payload.
         */
        get selectedShipping() {
          if (self.selected_shipping) {
            const selectedShipping = self.selected_shipping;
            const pickupPoint = self.selectedPickupPoint;
            const selectionFields = Array.from(
              selectedShipping.selection_fields.values()
            ).map((field) => ({
              id: field.id,
              value: field.value,
            }));

            return {
              module: selectedShipping.module?.id || null,
              pickup_point: pickupPoint ? getSnapshot(pickupPoint) : null,
              selection_fields: selectionFields,
            };
          }

          return null;
        },
        get shippingCustomerFields() {
          return [...self.shipping_customer_fields.values()];
        },
        get shippingAddressFields() {
          return self.shippingCustomerFields.filter(
            (field) =>
              field.id !== FormFieldId.DELIVERY_COUNTRY &&
              field.id !== FormFieldId.YOUR_REFERENCE
          );
        },
        get shippingInfoAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.SHIPPING_INFO_MODULE);
        },
        get shippingMethodAlerts() {
          return self.getModuleAlerts(
            CheckoutPageModule.SHIPPING_METHOD_MODULE
          );
        },
        get summaryAlerts() {
          return self.getModuleAlerts(CheckoutPageModule.SUMMARY_MODULE);
        },
        get canChangePaymentProvider() {
          return self.payment_providers && self.payment_providers.length > 1;
        },
        get validShipping() {
          return !!self.pickupPoints.get(self.pickupPointIdentifier);
        },
        getCustomerField(fieldId) {
          const field = self.customer_fields.get(fieldId);
          return {
            field,
            value: field && field.value,
          };
        },
        getShippingCustomerField(fieldId) {
          const field = self.shipping_customer_fields.get(fieldId);
          return {
            field,
            value: field && field.value,
          };
        },
        /**
         * Extract address data from order fields
         * @param {boolean} isBilling - Whether to extract billing or delivery address
         */
        extractAddressData(isBilling) {
          const prefix = isBilling ? 'BILLING' : 'DELIVERY';
          const getFieldInfo = (fieldId) => {
            const field = self.customer_fields.get(fieldId);
            return {
              value: field?.value || '',
              required: field?.required || false,
            };
          };

          const countryFieldId = isBilling
            ? FormFieldId.BILLING_COUNTRY
            : FormFieldId.DELIVERY_COUNTRY;
          const countryField = self.customer_fields.get(countryFieldId);
          const countryRawValue = countryField?.value;

          // Resolve country display label from:
          // - objects with { name }
          // - plain strings
          // - numeric ids via options[{value,label}]
          let countryDisplayValue = '';
          if (typeof countryRawValue === 'object' && countryRawValue?.name) {
            countryDisplayValue = countryRawValue.name;
          } else if (typeof countryRawValue === 'string') {
            countryDisplayValue = countryRawValue;
          } else if (
            typeof countryRawValue === 'number' ||
            typeof countryRawValue === 'boolean'
          ) {
            const opts = countryField?.options || [];
            const match = opts.find((o) => o.value === countryRawValue);
            countryDisplayValue = match?.label || '';
          }

          const companyInfo = getFieldInfo(FormFieldId[`${prefix}_COMPANY`]);
          const firstNameInfo = getFieldInfo(
            FormFieldId[`${prefix}_FIRST_NAME`]
          );
          const lastNameInfo = getFieldInfo(FormFieldId[`${prefix}_LAST_NAME`]);
          const streetAddressInfo = getFieldInfo(
            FormFieldId[`${prefix}_STREET_ADDRESS`]
          );
          const postalCodeInfo = getFieldInfo(
            FormFieldId[`${prefix}_POSTAL_CODE`]
          );
          const cityInfo = getFieldInfo(FormFieldId[`${prefix}_CITY`]);
          const phoneInfo = getFieldInfo(FormFieldId[`${prefix}_PHONE`]);
          const emailInfo = isBilling
            ? getFieldInfo(FormFieldId.BILLING_EMAIL)
            : { value: '', required: false };
          const countryInfo = {
            rawValue: countryRawValue,
            displayValue: countryDisplayValue,
            required: countryField?.required || false,
          };

          let vatNumberInfo = { value: '', required: false };
          let vatFieldModel = null;
          if (isBilling) {
            const vatFieldId = FormFieldId.BILLING_VAT_NUMBER;
            vatFieldModel = self.customer_fields.get(vatFieldId);
            vatNumberInfo = getFieldInfo(vatFieldId);
          }

          const isCompanyField = self.customer_fields.get(
            isBilling
              ? FormFieldId.BILLING_IS_COMPANY
              : FormFieldId.DELIVERY_IS_COMPANY
          );
          const companyFlagVal = isCompanyField?.value;
          const isCompanyCheckout =
            companyFlagVal === true ||
            companyFlagVal === 1 ||
            companyFlagVal === '1' ||
            companyFlagVal === 'true';

          const vatStr = String(vatNumberInfo.value ?? '').trim();
          const vatEmpty = vatStr === '';
          const vatMissing =
            isBilling &&
            vatFieldModel?.active &&
            vatEmpty &&
            (vatNumberInfo.required || isCompanyCheckout);

          const hasMissingRequiredFields =
            (companyInfo.required && !companyInfo.value) ||
            (firstNameInfo.required && !firstNameInfo.value) ||
            (lastNameInfo.required && !lastNameInfo.value) ||
            (streetAddressInfo.required && !streetAddressInfo.value) ||
            (postalCodeInfo.required && !postalCodeInfo.value) ||
            (cityInfo.required && !cityInfo.value) ||
            (countryInfo.required &&
              (countryInfo.rawValue === null ||
                countryInfo.rawValue === undefined ||
                countryInfo.rawValue === '')) ||
            (phoneInfo.required && !phoneInfo.value) ||
            (emailInfo.required && !emailInfo.value) ||
            vatMissing;

          return {
            company: String(companyInfo.value || ''),
            firstName: String(firstNameInfo.value || ''),
            lastName: String(lastNameInfo.value || ''),
            streetAddress: String(streetAddressInfo.value || ''),
            postalCode: String(postalCodeInfo.value || ''),
            city: String(cityInfo.value || ''),
            country: String(countryInfo.displayValue || ''),
            phone: String(phoneInfo.value || ''),
            email: String(emailInfo.value || ''),
            vatNumber: vatStr,
            hasMissingRequiredFields,
          };
        },
        get billingAddressData() {
          return this.extractAddressData(true);
        },
        get deliveryAddressData() {
          return this.extractAddressData(false);
        },
        get isBillingAddressValid() {
          return !this.billingAddressData.hasMissingRequiredFields;
        },
        get isDeliveryAddressValid() {
          return !this.deliveryAddressData.hasMissingRequiredFields;
        },
      },
      actions: {
        /**
         * Pickup Point Fetching Flow:
         *
         * This method sets up watchers to automatically fetch pickup points when needed.
         *
         * Flow:
         * 1. User enters/changes address → pickupPointIdentifier changes
         * 2. Watcher detects change → checks if identifier exists in cache
         * 3. If not cached → fetchPickupPoints() is called
         * 4. After successful fetch → handlePickupPointsLoaded() auto-selects and validates
         * 5. Component re-renders (via MobX observer) to show pickup points
         * 6. User manually selects → handlePickupPointUpdate() calls onOrderUpdate()
         *
         * Watchers monitor:
         * - selected_shipping (shipping method changes)
         * - selected_payment (payment provider changes)
         * - shipping_customer_fields (address fields for KUSTOM)
         * - customer_fields (address fields for PAYTRAIL)
         *
         * Fetching only occurs when:
         * - Shipping module requires pickup points (requires_pickup_point === true)
         * - Customer address is provided (postCode exists)
         * - Identifier doesn't exist in cache (not already LOADED)
         */
        afterAttach() {
          if (self.cartIsEmpty) {
            return;
          }

          // Helper to check if we should fetch pickup points
          const shouldFetch = () => {
            // Ensure shipping_options is loaded before checking module reference
            if (!self.shipping_options || self.shipping_options.length === 0) {
              return false;
            }

            try {
              // Safely check if module is resolved and has requires_pickup_point
              const module = getSelectedShippingModule();
              if (!module) {
                return false;
              }

              // Check if module reference is resolved (safeReference returns null if unresolved)
              const requiresPickupPoint = module.requires_pickup_point;
              const hasAddress = self.customerAddress?.postCode;
              const identifier = self.pickupPointIdentifier;
              const alreadyFetched =
                self.pickupPointState.get(identifier) === RequestState.LOADED;

              return requiresPickupPoint && hasAddress && !alreadyFetched;
            } catch (error) {
              // Reference might not be resolved yet, return false
              return false;
            }
          };

          // Helper to handle changes that affect pickup point identifier
          // This is wrapped in an action to allow model modifications
          const handleIdentifierChange = () => {
            self.handlePickupPointIdentifierChange(shouldFetch);
          };

          // Watch for changes to selected_shipping (shipping method changes)
          onSnapshot(self.selected_shipping, handleIdentifierChange);

          // Watch for changes to selected_payment (payment provider changes)
          onSnapshot(self.selected_payment, handleIdentifierChange);

          // Watch for changes to customer address fields that affect identifier
          onSnapshot(self.shipping_customer_fields, handleIdentifierChange);
          onSnapshot(self.customer_fields, handleIdentifierChange);

          // Check initial state - fetch if conditions are already met
          if (shouldFetch()) {
            self.fetchPickupPoints();
          }
        },
        cancelPickups: () => {
          if (source) {
            source.cancel();
          }

          source = axios.CancelToken.source();
        },
        /**
         * Checkout may omit DELIVERY_COMPANY from customer_fields until the server sees
         * delivery_customer_is_company true. Clone field metadata so the client can set company locally.
         */
        ensureDeliveryCompanyCustomerField() {
          const map = self.customer_fields;
          if (!map) return;
          if (map.get(FormFieldId.DELIVERY_COMPANY) != null) return;
          const template =
            map.get(FormFieldId.BILLING_COMPANY) ||
            map.get(FormFieldId.DELIVERY_STREET_ADDRESS);
          if (!template) return;
          const snap = getSnapshot(template);
          map.set(FormFieldId.DELIVERY_COMPANY, {
            ...snap,
            id: FormFieldId.DELIVERY_COMPANY,
            value: null,
            previousValue: null,
          });
        },
        /**
         * Handle Pickup Point Identifier Change:
         *
         * Action method to handle changes that affect the pickup point identifier.
         * This is called from onSnapshot watchers and must be an action to allow model modifications.
         *
         * - If fetch is needed: preserves current pickup point ID and triggers fetch
         * - If already loaded: ensures a pickup point is selected for non-KUSTOM providers
         */
        handlePickupPointIdentifierChange(shouldFetch) {
          if (shouldFetch()) {
            // Before fetching, preserve current pickup point ID if it exists
            // This ensures we can restore it after fetch if it exists in the new list
            const currentPickupPointId = self.selected_shipping?.pickup_point;
            if (currentPickupPointId) {
              // Preserve the ID if not already preserved
              if (!self.preservedPickupPointId) {
                self.preservedPickupPointId = currentPickupPointId;
              }
              // Ensure the ID is still set in selected_shipping.pickup_point
              // This is important for PUT requests that happen before fetch completes
              // The selectedPickupPoint getter will search all lists to find it
              if (
                self.selected_shipping?.pickup_point !== currentPickupPointId
              ) {
                self.selected_shipping.updateSelectedPickupPoint({
                  id: currentPickupPointId,
                });
              }
            }
            self.fetchPickupPoints();
          }
          // Note: When points are already loaded, validation is handled by validateShipping()
          // which is called after order updates, so no need to duplicate logic here
        },
        /**
         * Fetch Pickup Points:
         *
         * Fetches pickup points from the backend API for the current identifier.
         * Only fetches if:
         * - Not already loaded or loading for this identifier
         * - Customer address is provided
         * - Shipping module, payment module, and country code are available
         *
         * The fetched data is stored in pickupPoints map keyed by identifier.
         * After successful fetch, handlePickupPointsLoaded() is called to auto-select.
         */
        fetchPickupPoints: flow(function* fetchPickupPoints() {
          const customerAddress = self.customerAddress;
          const countryCode =
            getRoot(self).countryStore.activeCountry?.iso_code_2;
          const selectedShippingModule = getSelectedShippingModule();
          const selectedPaymentModule = getSelectedPaymentModule();

          // Get identifier - if it changes during fetch, that's ok, we'll fetch again
          const identifier = self.pickupPointIdentifier;

          // Don't fetch if already loaded for this identifier
          // But if we have an invalid pickup point, we still need to validate
          const currentPickupPoint = self.selected_shipping?.pickup_point;
          const currentList = self.pickupPoints.get(identifier) || [];
          const pickupPointInvalid =
            currentPickupPoint &&
            !currentList.some((pp) => pp.unique_id === currentPickupPoint);

          if (
            self.pickupPointState.get(identifier) === RequestState.LOADED &&
            !pickupPointInvalid
          ) {
            return false;
          }

          if (
            self.pickupPointState.get(identifier) === RequestState.LOADED &&
            pickupPointInvalid
          ) {
            // Points are loaded but pickup point is invalid - just validate without fetching
            yield self.handlePickupPointsLoaded();
            return self._pickupPointNeedsSync;
          }

          // If already loading, wait for it to complete
          if (self.pickupPointState.get(identifier) === RequestState.LOADING) {
            // Wait for loading to complete
            while (
              self.pickupPointState.get(identifier) === RequestState.LOADING
            ) {
              yield new Promise((resolve) => setTimeout(resolve, 50));
            }
            return self._pickupPointNeedsSync;
          }

          // Check prerequisites
          if (
            !customerAddress?.postCode ||
            !selectedShippingModule ||
            !countryCode ||
            !selectedPaymentModule
          ) {
            return false;
          }

          self.cancelPickups();

          self.pickupPointState.set(identifier, RequestState.LOADING);

          const payload = {
            shippingModuleId: selectedShippingModule.id,
            paymentModuleId: selectedPaymentModule,
            countryCode,
            ...customerAddress,
          };

          try {
            const pickupPoints = yield getEnv(self).apiWrapper.request(
              `shipping/pickup-points`,
              {
                cancelToken: source.token,
                params: payload,
              }
            );

            self.pickupPoints.set(identifier, pickupPoints || []);
            self.pickupPointState.set(identifier, RequestState.LOADED);

            // Auto-select pickup point after successful fetch
            if (pickupPoints && pickupPoints.length > 0) {
              yield self.handlePickupPointsLoaded();
            }
          } catch (error) {
            if (axios.isCancel(error)) {
              self.pickupPointState.set(identifier, RequestState.NONE);
            } else {
              self.pickupPointState.set(identifier, RequestState.ERROR);
              throw error;
            }
          }
        }),
        /**
         * Handle Pickup Points Loaded:
         *
         * Auto-selects pickup point after successful fetch based on payment provider.
         *
         * Selection priority order (for non-KUSTOM providers):
         * 1. Backend-provided ID (currentPickupPointId) - Always respect server state (e.g., on page load)
         * 2. Client-preserved ID (preservedPickupPointId) - Maintain selection across provider changes
         * 3. First pickup point - Fallback when no valid selection exists
         *
         * Provider-specific behavior:
         * - KUSTOM: No auto-selection (no pre-chosen radio button)
         *   - User must manually select a pickup point
         *   - Invalid selections are cleared and backend sync is requested
         * - PAYTRAIL (and other non-KUSTOM providers): Follow priority order above
         *   - Ensures something is always selected in the UI
         *
         * Returns: true if backend sync is needed (invalid pickup point was cleared), false otherwise
         *
         * This is called automatically after fetchPickupPoints() succeeds.
         */
        handlePickupPointsLoaded: flow(function* handlePickupPointsLoaded() {
          const currentPickupPointId = self.selected_shipping?.pickup_point;
          const preservedPickupPointId = self.preservedPickupPointId;
          const pickupPointList = self.currentPickupPointList;
          const isKUSTOM = self.paymentProvider === OrderPaymentProvider.KUSTOM;

          // Reset sync flag
          self._pickupPointNeedsSync = false;

          // For KUSTOM: Don't auto-select (no pre-chosen radio)
          if (isKUSTOM) {
            // Check if current pickup point is invalid and clear it if needed
            if (
              currentPickupPointId &&
              !pickupPointList.some(
                (pp) => pp.unique_id === currentPickupPointId
              )
            ) {
              // Invalid pickup point exists - clear it
              self.updateSelectedShipping({ pickupPointId: null });
              self.preservedPickupPointId = null;
              self._pickupPointNeedsSync = true;
              return;
            }
            // Clear preserved ID for KUSTOM since we don't auto-select
            self.preservedPickupPointId = null;
            return;
          }

          // For non-KUSTOM providers (PAYTRAIL, etc.): Priority order for selection:
          // 1. Backend-provided ID (currentPickupPointId) - respect server state
          // 2. Client-preserved ID (preservedPickupPointId) - maintain selection across provider changes
          // 3. First pickup point - fallback when no valid selection exists
          if (pickupPointList.length > 0) {
            // First check if current pickup point (from backend) is valid in the list
            if (
              currentPickupPointId &&
              pickupPointList.some(
                (pp) => pp.unique_id === currentPickupPointId
              )
            ) {
              // Current ID exists in list - keep it (backend data should be obeyed)
              return;
            }

            // Check if preserved pickup point ID exists in the new list
            if (
              preservedPickupPointId &&
              pickupPointList.some(
                (pp) => pp.unique_id === preservedPickupPointId
              )
            ) {
              // Preserved ID exists in new list - select it to maintain selection across provider changes
              self.updateSelectedShipping({
                pickupPointId: preservedPickupPointId,
              });
              return;
            } else {
              // No valid current or preserved ID - select first as fallback
              const firstPickupPoint = pickupPointList[0];
              self.updateSelectedShipping({
                pickupPointId: firstPickupPoint.unique_id,
              });
              if (currentPickupPointId) {
                self._pickupPointNeedsSync = true;
              }
              return;
            }
          }

          if (currentPickupPointId && pickupPointList.length === 0) {
            // If no pickup points available but one was selected, clear it
            self.updateSelectedShipping({ pickupPointId: null });
            self.preservedPickupPointId = null;
            self._pickupPointNeedsSync = true;
            return;
          }
        }),
        updateSelectedPayment({
          paymentMethodGroupId,
          paymentProviderId,
          paymentMethodId,
        }) {
          if (paymentMethodGroupId !== undefined) {
            if (paymentMethodGroupId === null) {
              self.selected_payment.group = null;
              self.selected_payment.method = null;
            } else {
              self.selected_payment.updatePaymentMethodGroup({
                id: paymentMethodGroupId,
                groups: self.payment_method_groups,
              });
            }
          }

          if (paymentMethodId !== undefined) {
            if (paymentMethodId === null) {
              self.selected_payment.method = null;
            } else {
              self.selected_payment.updatePaymentMethod({
                id: paymentMethodId,
                methods: self.payment_method_groups,
              });
            }
          }

          if (paymentProviderId !== undefined) {
            self.selected_payment.updatePaymentProvider({
              id: paymentProviderId,
              providers: self.payment_providers,
            });
          }
        },
        setApplePaySupportStatus({ completed, supported }) {
          self.applePaySupportCheckCompleted = completed;
          self.applePaySupported = supported;
        },
        updateSelectedShipping({ shippingModuleId, pickupPointId }) {
          if (shippingModuleId) {
            self.selected_shipping.updateSelectedModule({
              id: shippingModuleId,
              options: self.shipping_options,
            });
            // Clear preserved pickup point ID when shipping module changes
            // (different shipping modules have different pickup points)
            self.preservedPickupPointId = null;
            self.selected_shipping.ensureSelectionFieldsFromModule(
              self.selected_shipping.module
            );
          }

          if (pickupPointId !== undefined) {
            self.selected_shipping.updateSelectedPickupPoint({
              id: pickupPointId,
            });
            // Preserve the selected pickup point ID to maintain selection across provider changes
            // Only preserve if a valid ID is set (not null)
            if (pickupPointId) {
              self.preservedPickupPointId = pickupPointId;
            } else {
              self.preservedPickupPointId = null;
            }
          }
        },
        setScrollToMethodId(id) {
          self.scroll_to_method = id;
        },
      },
    };
  });

const OrderCheckout = types.snapshotProcessor(OrderCheckoutModel, {
  preProcessor(snapshot) {
    const convertItemsToMap = (items) => {
      return items.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    };

    return {
      ...snapshot,
      customer_fields: convertItemsToMap(snapshot.customer_fields),
      extra_fields: convertItemsToMap(snapshot.extra_fields),
      shipping_customer_fields: convertItemsToMap(
        snapshot.shipping_customer_fields
      ),
    };
  },
});

export default OrderCheckout;
